import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Server,
  Cpu,
  HardDrive,
  MemoryStick,
  MoreHorizontal,
  Trash2,
  Eye,
  Wifi,
  WifiOff,
  AlertTriangle,
  Activity,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface InfraNode {
  id: string
  name: string
  type: string
  hostname: string | null
  ipAddress: string | null
  os: string | null
  status: string
  cpuUsage: number | null
  memoryUsage: number | null
  diskUsage: number | null
  uptimeSeconds: number | null
  services: Record<string, unknown> | null
  tags: string[]
  lastSeenAt: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

interface InfraStats {
  total: number
  online: number
  offline: number
  degraded: number
  unknown: number
  byType: Array<{ type: string; count: number }>
  avgCpuUsage: number | null
  avgMemoryUsage: number | null
  avgDiskUsage: number | null
}

// ============================================
// Helpers
// ============================================

const nodeTypeLabels: Record<string, string> = {
  server: 'Server',
  container: 'Container',
  vm: 'Virtual Machine',
  switch: 'Switch',
  firewall: 'Firewall',
  ap: 'Access Point',
}

function statusBadge(status: string) {
  switch (status) {
    case 'online':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Online</Badge>
    case 'degraded':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Degraded</Badge>
    case 'offline':
      return <Badge variant="destructive">Offline</Badge>
    case 'unknown':
      return <Badge variant="secondary">Unknown</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function statusIcon(status: string) {
  switch (status) {
    case 'online':
      return <Wifi className="h-4 w-4 text-green-500" />
    case 'degraded':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />
    case 'offline':
      return <WifiOff className="h-4 w-4 text-red-500" />
    default:
      return <Activity className="h-4 w-4 text-muted-foreground" />
  }
}

function formatUptime(seconds: number | null): string {
  if (seconds === null) return '-'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) return `${days}d ${hours}h`
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}

function formatLastSeen(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function usageColor(value: number | null): string {
  if (value === null) return 'text-muted-foreground'
  if (value >= 90) return 'text-red-500'
  if (value >= 75) return 'text-orange-500'
  if (value >= 50) return 'text-yellow-500'
  return 'text-green-500'
}

function usageBarColor(value: number | null): string {
  if (value === null) return 'bg-muted'
  if (value >= 90) return 'bg-red-500'
  if (value >= 75) return 'bg-orange-500'
  if (value >= 50) return 'bg-yellow-500'
  return 'bg-green-500'
}

function UsageGauge({ label, value, icon }: { label: string; value: number | null; icon: React.ReactNode }) {
  const pct = value ?? 0
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1 text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <span className={`font-medium ${usageColor(value)}`}>
          {value !== null ? `${value.toFixed(1)}%` : '-'}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${usageBarColor(value)}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  )
}

// ============================================
// Add Node Dialog
// ============================================

function AddNodeDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState('server')
  const [hostname, setHostname] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [os, setOs] = useState('')
  const [nodeStatus, setNodeStatus] = useState('unknown')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/infrastructure/nodes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['infrastructure'] })
      toast({ title: 'Node added', description: 'New infrastructure node has been added' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to add node', variant: 'destructive' }),
  })

  function resetForm() {
    setName('')
    setType('server')
    setHostname('')
    setIpAddress('')
    setOs('')
    setNodeStatus('unknown')
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Add Node</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Infrastructure Node</DialogTitle>
          <DialogDescription>Register a new server, VM, container, or network device.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="web-server-01" />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="server">Server</SelectItem>
                  <SelectItem value="container">Container</SelectItem>
                  <SelectItem value="vm">Virtual Machine</SelectItem>
                  <SelectItem value="switch">Switch</SelectItem>
                  <SelectItem value="firewall">Firewall</SelectItem>
                  <SelectItem value="ap">Access Point</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hostname</Label>
              <Input value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="srv01.local" />
            </div>
            <div className="space-y-2">
              <Label>IP Address</Label>
              <Input value={ipAddress} onChange={(e) => setIpAddress(e.target.value)} placeholder="192.168.1.10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>OS</Label>
              <Input value={os} onChange={(e) => setOs(e.target.value)} placeholder="Ubuntu 22.04" />
            </div>
            <div className="space-y-2">
              <Label>Initial Status</Label>
              <Select value={nodeStatus} onValueChange={setNodeStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="degraded">Degraded</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              name,
              type,
              hostname: hostname || undefined,
              ipAddress: ipAddress || undefined,
              os: os || undefined,
              status: nodeStatus,
            })}
            disabled={!name || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding...' : 'Add Node'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Node Detail Dialog
// ============================================

function NodeDetailDialog({ node, onClose }: { node: InfraNode; onClose: () => void }) {
  const [newStatus, setNewStatus] = useState(node.status)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const statusMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.patch(`/infrastructure/nodes/${node.id}/status`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['infrastructure'] })
      toast({ title: 'Status updated', description: `Node status changed to ${newStatus}` })
      onClose()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' }),
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {statusIcon(node.status)}
            {node.name}
          </DialogTitle>
          <DialogDescription>{nodeTypeLabels[node.type] || node.type} - {node.hostname || node.ipAddress || 'No address'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <div className="mt-0.5">{statusBadge(node.status)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="text-sm font-medium">{nodeTypeLabels[node.type] || node.type}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Hostname</p>
              <p className="text-sm font-medium font-mono">{node.hostname || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">IP Address</p>
              <p className="text-sm font-medium font-mono">{node.ipAddress || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Operating System</p>
              <p className="text-sm font-medium">{node.os || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Uptime</p>
              <p className="text-sm font-medium">{formatUptime(node.uptimeSeconds)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Seen</p>
              <p className="text-sm font-medium">{formatLastSeen(node.lastSeenAt)}</p>
            </div>
          </div>

          {/* Resource Gauges */}
          <div className="space-y-3 border rounded-lg p-4">
            <p className="text-sm font-medium">Resource Usage</p>
            <UsageGauge label="CPU" value={node.cpuUsage} icon={<Cpu className="h-3 w-3" />} />
            <UsageGauge label="Memory" value={node.memoryUsage} icon={<MemoryStick className="h-3 w-3" />} />
            <UsageGauge label="Disk" value={node.diskUsage} icon={<HardDrive className="h-3 w-3" />} />
          </div>

          {node.tags.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Tags</p>
              <div className="flex flex-wrap gap-1">
                {node.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Status Update */}
          <div className="space-y-3 border rounded-lg p-4">
            <p className="text-sm font-medium">Update Status</p>
            <div className="flex items-center gap-3">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="degraded">Degraded</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => statusMutation.mutate({ status: newStatus })}
                disabled={newStatus === node.status || statusMutation.isPending}
              >
                {statusMutation.isPending ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Node Card (Grid View)
// ============================================

function NodeCard({ node, onClick }: { node: InfraNode; onClick: () => void }) {
  return (
    <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={onClick}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {statusIcon(node.status)}
            <CardTitle className="text-sm font-medium truncate">{node.name}</CardTitle>
          </div>
          {statusBadge(node.status)}
        </div>
        <p className="text-xs text-muted-foreground">
          {nodeTypeLabels[node.type] || node.type}
          {node.ipAddress && <span className="font-mono ml-1">({node.ipAddress})</span>}
        </p>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <UsageGauge label="CPU" value={node.cpuUsage} icon={<Cpu className="h-3 w-3" />} />
        <UsageGauge label="Memory" value={node.memoryUsage} icon={<MemoryStick className="h-3 w-3" />} />
        <UsageGauge label="Disk" value={node.diskUsage} icon={<HardDrive className="h-3 w-3" />} />
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span>Uptime: {formatUptime(node.uptimeSeconds)}</span>
          <span>Seen: {formatLastSeen(node.lastSeenAt)}</span>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Main Page
// ============================================

export function InfrastructurePage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedNode, setSelectedNode] = useState<InfraNode | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch nodes
  const { data: nodesData, isLoading } = useQuery({
    queryKey: ['infrastructure', 'nodes', typeFilter, statusFilter],
    queryFn: () => apiClient.get<{ data: InfraNode[]; meta: unknown }>('/infrastructure/nodes', {
      ...(typeFilter !== 'all' && { type: typeFilter }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
      limit: 100,
    }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['infrastructure', 'stats'],
    queryFn: () => apiClient.get<InfraStats>('/infrastructure/stats'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/infrastructure/nodes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['infrastructure'] })
      toast({ title: 'Deleted', description: 'Node has been removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete node', variant: 'destructive' }),
  })

  const nodes = nodesData?.data?.data || nodesData?.data || []
  const stats = statsData?.data || null

  const filtered = Array.isArray(nodes)
    ? nodes.filter((n: InfraNode) =>
        n.name.toLowerCase().includes(search.toLowerCase()) ||
        (n.hostname || '').toLowerCase().includes(search.toLowerCase()) ||
        (n.ipAddress || '').toLowerCase().includes(search.toLowerCase())
      )
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Infrastructure</h1>
          <p className="text-muted-foreground">Monitor servers, VMs, containers, and network devices</p>
        </div>
        <AddNodeDialog />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Nodes</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.total ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.byType?.map((t) => `${t.count} ${t.type}`).join(', ') || 'No nodes'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Wifi className="h-4 w-4 text-green-500" />
              <p className="text-xs text-muted-foreground">Online</p>
            </div>
            <div className="text-2xl font-bold text-green-500 mt-1">{stats?.online ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.total ? `${Math.round(((stats.online || 0) / stats.total) * 100)}% uptime` : '0% uptime'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <p className="text-xs text-muted-foreground">Degraded</p>
            </div>
            <div className="text-2xl font-bold text-yellow-500 mt-1">{stats?.degraded ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">{stats?.offline ?? 0} offline</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Cpu className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Avg Utilization</p>
            </div>
            <div className="text-2xl font-bold text-blue-500 mt-1">
              {stats?.avgCpuUsage !== null && stats?.avgCpuUsage !== undefined ? `${stats.avgCpuUsage}%` : '-'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Mem: {stats?.avgMemoryUsage !== null && stats?.avgMemoryUsage !== undefined ? `${stats.avgMemoryUsage}%` : '-'}
              {' / '}
              Disk: {stats?.avgDiskUsage !== null && stats?.avgDiskUsage !== undefined ? `${stats.avgDiskUsage}%` : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search nodes..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[170px]"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="server">Server</SelectItem>
            <SelectItem value="container">Container</SelectItem>
            <SelectItem value="vm">Virtual Machine</SelectItem>
            <SelectItem value="switch">Switch</SelectItem>
            <SelectItem value="firewall">Firewall</SelectItem>
            <SelectItem value="ap">Access Point</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="degraded">Degraded</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center border rounded-md">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            className="rounded-r-none"
            onClick={() => setViewMode('grid')}
          >
            Grid
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="rounded-l-none"
            onClick={() => setViewMode('list')}
          >
            List
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-2 w-full" />
                  <Skeleton className="h-2 w-full" />
                  <Skeleton className="h-2 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Server className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No nodes found</p>
            <p className="text-sm text-muted-foreground">Add your first infrastructure node to get started</p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((node: InfraNode) => (
            <NodeCard key={node.id} node={node} onClick={() => setSelectedNode(node)} />
          ))}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Node</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">IP / Hostname</th>
                  <th className="px-4 py-3 font-medium">CPU</th>
                  <th className="px-4 py-3 font-medium">Memory</th>
                  <th className="px-4 py-3 font-medium">Disk</th>
                  <th className="px-4 py-3 font-medium">Uptime</th>
                  <th className="px-4 py-3 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((node: InfraNode) => (
                  <tr key={node.id} className="border-b cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setSelectedNode(node)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {statusIcon(node.status)}
                        <span className="font-medium text-sm">{node.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{nodeTypeLabels[node.type] || node.type}</td>
                    <td className="px-4 py-3">{statusBadge(node.status)}</td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {node.ipAddress || node.hostname || '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm font-mono ${usageColor(node.cpuUsage)}`}>
                      {node.cpuUsage !== null ? `${node.cpuUsage.toFixed(1)}%` : '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm font-mono ${usageColor(node.memoryUsage)}`}>
                      {node.memoryUsage !== null ? `${node.memoryUsage.toFixed(1)}%` : '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm font-mono ${usageColor(node.diskUsage)}`}>
                      {node.diskUsage !== null ? `${node.diskUsage.toFixed(1)}%` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{formatUptime(node.uptimeSeconds)}</td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedNode(node); }}>
                            <Eye className="mr-2 h-4 w-4" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(node.id); }}>
                            <Trash2 className="mr-2 h-4 w-4" /> Remove Node
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Node Detail Dialog */}
      {selectedNode && (
        <NodeDetailDialog
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  )
}
